import React, { useState } from 'react';
import { Stack, Switch, Typography } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { CompleteStepDialog } from '../CompleteStepDialog';
import { usePatientIntakeStatusContext, useStepEdit } from '../hooks';
import { BaseSteps } from './BaseSteps';

type ToggleHiddenStepsSwitchProps = {
  showHiddenSteps: boolean;
  setShowHiddenSteps: React.Dispatch<React.SetStateAction<boolean>>;
};

const ToggleHiddenStepsSwitch: React.FC<ToggleHiddenStepsSwitchProps> = ({ showHiddenSteps, setShowHiddenSteps }) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();

  if (!isHealthcareProvider) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography variant="caption">Show disabled steps</Typography>
      <Switch
        checked={showHiddenSteps}
        onChange={() => setShowHiddenSteps(!showHiddenSteps)}
        title="Show disabled steps"
        size="small"
      />
    </Stack>
  );
};

export const BaseStepsSection: React.FC = () => {
  const { editingStep, setEditingStep } = useStepEdit();
  const [showHiddenSteps, setShowHiddenSteps] = useState(false);

  const handleOnEdit = (baseField: keyof IntakeStatusBaseSteps) => {
    setEditingStep(baseField);
  };

  const shouldShowEditingStep = !!editingStep && editingStep.step.stepType === 'BASE_STEP';

  return (
    <Stack sx={{ mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">The Basics</Typography>
        <ToggleHiddenStepsSwitch showHiddenSteps={showHiddenSteps} setShowHiddenSteps={setShowHiddenSteps} />
      </Stack>

      <BaseSteps onEdit={handleOnEdit} showHiddenSteps={showHiddenSteps} />

      {shouldShowEditingStep && (
        <CompleteStepDialog
          open={shouldShowEditingStep}
          onClose={() => setEditingStep(null)}
          onSuccess={() => setEditingStep(null)}
          {...editingStep}
        />
      )}
    </Stack>
  );
};
